import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';

export interface CountryOption {
  code: string;
  name: string;
}

export interface CountryAutocompleteMultiSelectProps {
  countries: CountryOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const countryFlag = (code: string) =>
  code
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
    .join('');

export const CountryAutocompleteMultiSelect: React.FC<CountryAutocompleteMultiSelectProps> = ({
  countries,
  selected,
  onChange,
  placeholder = 'Type to search countries...',
  className = '',
  disabled = false,
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedSet = new Set(selected);
  const filtered = countries.filter(
    (c) =>
      !selectedSet.has(c.code) &&
      (query.length === 0 ||
        c.name.toLowerCase().includes(query.toLowerCase()) ||
        c.code.toLowerCase().includes(query.toLowerCase()))
  ).slice(0, 12);

  useEffect(() => {
    setHighlightIndex(0);
  }, [query, filtered.length]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code: string) => {
    onChange([...selected, code]);
    setQuery('');
    setIsOpen(false);
  };

  const handleRemove = (code: string) => {
    onChange(selected.filter((c) => c !== code));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered[highlightIndex]) {
      e.preventDefault();
      handleSelect(filtered[highlightIndex].code);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex flex-wrap gap-2 mb-2">
        {selected.map((code) => {
          const c = countries.find((x) => x.code === code);
          return (
            <span
              key={code}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
            >
              <span className="text-lg">{countryFlag(code)}</span>
              <span>{c?.name || code}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(code)}
                  className="ml-1 p-0.5 rounded hover:bg-primary-200 dark:hover:bg-primary-800"
                  aria-label={`Remove ${c?.name || code}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          );
        })}
      </div>
      {!disabled && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            autoComplete="off"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
          {isOpen && (
            <ul
              ref={listRef}
              className="absolute z-50 mt-1 w-full max-h-48 overflow-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg"
            >
              {filtered.length === 0 ? (
                <li className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                  {query ? 'No matching countries' : 'Type to search'}
                </li>
              ) : (
                filtered.map((c, i) => (
                  <li key={c.code}>
                    <button
                      type="button"
                      onClick={() => handleSelect(c.code)}
                      className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-sm ${
                        i === highlightIndex ? 'bg-gray-100 dark:bg-gray-700' : ''
                      }`}
                    >
                      <span className="text-lg">{countryFlag(c.code)}</span>
                      <span>{c.name}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
